import { LineItem, Action as LineItemAction, reducer as lineItemReducer } from './lineItemsModel'
import * as PaymentScheduleModel from './paymentScheduleModel'
import { roundToPrecision } from '../../helpers/math'
import { EmailTemplateTypes, PricingTypes } from '../../common/constants'
import { arrayMove } from '@dnd-kit/sortable'
import { EmailTemplate } from '../EmailTemplates/emailTemplateModel'
import { EstimatedItem } from '../../domain/workareaItem'
import { CatalogItem } from '../../domain/catalogItem'
import { PaymentScheduleOptions, PaymentScheduleRow } from '../../domain/proposals/settings/proposalSettings'
import { InvoicingSystem } from '../../domain/invoicing/settings/invoicingSettings'
import { appendAttachment, ProposalAttachment } from './proposalAttachmentsModel'

export interface ProposalCreationParameters {
  jobId: number
  customerId?: number
  estimatedHours?: number
  internalName?: string
  coverLetter?: string
  attachments?: ProposalAttachment[]
  attachmentIds?: string[]
  termsAndConditions: string
  includePricingTypeInformation: boolean
  showCompanyAddress: boolean
  existingProposalGeneratedAt?: Date
  lineItems: LineItem[]
  downPayment: DownPaymentParameters
  finalPaymentNote: string
  infoLinks: InfoLink[]
  paymentScheduleOptions: PaymentScheduleOptions
  estimateSalesTax: boolean
  redirectToDownPaymentCheckoutOnApproval: boolean
}

export type ServerState = {
  estimatedItems: EstimatedItem[]
  catalogItems: CatalogItem[]
}

export const defaultState = (): ProposalCreationParameters => ({
  jobId: 0,
  termsAndConditions: '',
  includePricingTypeInformation: false,
  showCompanyAddress: false,
  lineItems: [],
  downPayment: {
    autoSubmitDownPaymentInvoice: false,
    enableCardPayments: false,
    enableBankTransfers: false,
    paymentDueInDays: 0,
    note: '',
    percentage: 0,
    costCodesUnlinkedToQbo: [],
    isCustomerLinkedToQbo: false,
    invoicingSystem: 'quickBooks',
    showAuthorizePaymentOnFile: false,
    requireAuthorizePaymentOnFile: false
  },
  finalPaymentNote: '',
  infoLinks: [],
  paymentScheduleOptions: PaymentScheduleModel.createDefaultPaymentScheduleOptions(),
  estimateSalesTax: false,
  redirectToDownPaymentCheckoutOnApproval: false,
})

export const warnings = (proposal: ProposalCreationParameters): string[] => {
  return proposal.existingProposalGeneratedAt
    ? ['preExistingProposal']
    : []
}

export interface Workarea { }
export interface Job {
  workareas: Workarea[]
}

export type InfoLink = {
  title: string,
  url: string,
  id: number,
  order: number
}

export enum ActionType {
  DataLoaded = 'dataLoaded',
  TermsAndConditionsChanged = 'termsAndConditionsChanged',
  IncludePricingTypeChanged = 'includePricingTypeChanged',
  ShowCompanyAddressChanged = 'showCompanyAddressChanged',
  LineItemChanged = 'lineItemChanged',
  AutoSubmitDownPaymentInvoiceChanged = 'autoSubmitDownPaymentInvoiceChanged',
  FinalPaymentNoteChanged = 'FinalPaymentNoteChanged',
  EstimateSalesTaxChanged = 'estimateSalesTaxChanged',
  LineItemMoved = 'lineItemMoved',
  RedirectToDownPaymentCheckoutOnApprovalChanged = 'redirectToDownPaymentCheckoutOnApprovalChanged',
  InternalNameChanged = 'internalNameChanged',
  CoverLetterChanged = 'coverLetterChanged',
  AttachmentsChanged = 'attachmentsChanged',
  AttachmentAdded = 'attachmentAdded',
}

export enum InfoLinkActionType {
  Deleted = "InfoLinkDeleted",
  Added = "InfoLinkAdded",
  TitleChanged = "InfoLinkTitleChanged",
  UrlChanged = "InfoLinkUrlChanged"
}

export const defaultEmailTemplate: EmailTemplate = {
  id: 0,
  guid: '00000000-0000-0000-0000-000000000000',
  name: 'Default Template',
  emailSubject: '{{customer_displayname}} Here is your Estimate {{project_no}} from {{salesperson_name}} at {{company_name}}',
  emailBody: `<p>{{customer_greeting}}</p><p>Thanks for working with us regarding your project! 
I’ve put together this proposal, and am submitting it to you for your review. 
When you click on the link below you will see the breakdown of the different sections (if there is more than one work area) of the estimate, and 
have the opportunity to turn off and on the work areas you’d like. 
When everything looks good to you, you can check the Approve box, enter your name in the field provided, and Confirm. 
That will finalize the work to be done and serve as your electronic signature and approval.
</p>    
<p>If you need me to edit or alter anything, contact me at {{salesperson_email}} and let me know what you’d like changed. </p>
<p>{{proposal_link}}</p>
<p>Thank you for the opportunity to work with you on your project!</p>
<p>Sincerely, </p>
<p>{{salesperson_name}}</p>
<p>{{salesperson_email}}</p>`,
  type: EmailTemplateTypes.Proposal,
}

export const defaultFollowUpEmailTemplates: EmailTemplate[] = [
  {
    id: 0,
    guid: '00000000-0000-0000-0000-000000000000',
    name: 'Follow-Up 1',
    emailSubject: 'Hi {{customer_displayname}}, did you see your proposal from {{company_name}}?',
    emailBody: '{{customer_greeting}}<br/><br/>I’m checking to make sure you got the proposal I sent over. <br/><br/>You can view it at the link below. <br/><br/>{{proposal_link}}<br/><br/>Send me an email at {{salesperson_email}} and I can help you if there’s anything you’d like to discuss.<br/><br/>Thank you!<br/><br/>{{salesperson_name}}',
    type: EmailTemplateTypes.Proposal
  },
  {
    id: 0,
    guid: '00000000-0000-0000-0000-000000000000',
    name: 'Follow-Up 2',
    emailSubject: 'Hi {{customer_displayname}}, friendly nudge you on your {{company_name}} proposal',
    emailBody: '{{customer_greeting}}<br/><br/>Have you had a chance to check out the proposal I put together for you? <br/><br/>You can always view it at the link below. <br/><br/>{{proposal_link}}<br/><br/>I’m available at {{salesperson_email}} to address any questions or concerns.<br/><br/>Thank you!<br/><br/>{{salesperson_name}}',
    type: EmailTemplateTypes.Proposal
  },
  {
    id: 0,
    guid: '00000000-0000-0000-0000-000000000000',
    name: 'Follow-Up 3',
    emailSubject: 'Final reminder for your {{company_name}} proposal',
    emailBody: '{{customer_greeting}}<br/><br/>As we approach the expiration of this proposal, I’m checking one more time to see if you had any questions on it. <br/><br/>This is the last reminder regarding this proposal. <br/><br/>{{proposal_link}}<br/><br/>I’m always here and ready to discuss any items that come up for you regarding this work. <br/><br/>Thank you for the opportunity!<br/><br/>{{salesperson_name}}<br/><br/>{{salesperson_email}}',
    type: EmailTemplateTypes.Proposal
  }
]


export type Action =
  | { type: ActionType.DataLoaded, value: State }
  | { type: ActionType.TermsAndConditionsChanged, value: string }
  | { type: ActionType.IncludePricingTypeChanged, value: boolean }
  | { type: ActionType.ShowCompanyAddressChanged, value: boolean }
  | { type: ActionType.AutoSubmitDownPaymentInvoiceChanged, value: boolean }
  | { type: ActionType.LineItemChanged, id: number, lineItemAction: LineItemAction }
  | { type: ActionType.FinalPaymentNoteChanged, value: string }
  | { type: ActionType.EstimateSalesTaxChanged, value: boolean }
  | { type: ActionType.LineItemMoved, id: number, over: number }
  | { type: ActionType.RedirectToDownPaymentCheckoutOnApprovalChanged, value: boolean }
  | { type: ActionType.InternalNameChanged, value: string }
  | { type: ActionType.CoverLetterChanged, value: string }
  | { type: ActionType.AttachmentsChanged, value: ProposalAttachment[] }
  | { type: ActionType.AttachmentAdded, value: ProposalAttachment }
  | { type: InfoLinkActionType.Deleted, id: number }
  | { type: InfoLinkActionType.Added }
  | { type: InfoLinkActionType.TitleChanged, title: string, id: number }
  | { type: InfoLinkActionType.UrlChanged, url: string, id: number }
  | { type: DownPaymentActionType.AutoSubmitDownPaymentInvoiceChanged, value: boolean }
  | { type: DownPaymentActionType.EnableCardPaymentsChanged, value: boolean }
  | { type: DownPaymentActionType.EnableBankTransfersChanged, value: boolean }
  | { type: DownPaymentActionType.ShowAuthorizePaymentOnFileChanged, value: boolean }
  | { type: DownPaymentActionType.RequireAuthorizePaymentOnFileChanged, value: boolean }
  | { type: DownPaymentActionType.SetPaymentDueInDaysChanged, value: number }
  | { type: DownPaymentActionType.NoteChanged, value: string }
  | { type: DownPaymentActionType.DownPaymentPercentageChanged, value: string }
  | PaymentScheduleModel.PaymentScheduleAction
  | PaymentScheduleModel.PaymentScheduleOptionsAction

export const setTermsAndConditions = (value: string): Action => ({ type: ActionType.TermsAndConditionsChanged, value })
export const setInternalName = (value: string): Action => ({ type: ActionType.InternalNameChanged, value })
export const setCoverLetter = (value: string): Action => ({ type: ActionType.CoverLetterChanged, value })
export const setAttachments = (value: ProposalAttachment[]): Action => ({ type: ActionType.AttachmentsChanged, value })

export const addAttachment = (value: ProposalAttachment): Action => ({ type: ActionType.AttachmentAdded, value })
export const setIncludePricingType = (value: boolean): Action => ({ type: ActionType.IncludePricingTypeChanged, value })
export const setShowCompanyAddress = (value: boolean): Action => ({ type: ActionType.ShowCompanyAddressChanged, value })
export const setLineItem = (id: number, lineItemAction: LineItemAction): Action => ({ type: ActionType.LineItemChanged, id, lineItemAction })
export const setFinalPaymentNote = (value: string): Action => ({ type: ActionType.FinalPaymentNoteChanged, value })
export const setEstimateSalesTax = (value: boolean): Action => ({ type: ActionType.EstimateSalesTaxChanged, value })
export const setRedirectToDownPaymentCheckoutOnApproval = (value: boolean): Action => ({ type: ActionType.RedirectToDownPaymentCheckoutOnApprovalChanged, value })
export const infoLinkDeleted = (id: number): Action => ({ type: InfoLinkActionType.Deleted, id: id })

export const reducer = (state: ProposalCreationParameters, action: Action): ProposalCreationParameters => {
  switch (action.type) {
    case ActionType.DataLoaded:
      return {
        ...action.value,
        paymentScheduleOptions: action.value.paymentScheduleOptions ?? PaymentScheduleModel.createDefaultPaymentScheduleOptions()
      }
    case ActionType.TermsAndConditionsChanged:
      return { ...state, termsAndConditions: action.value }
    case ActionType.InternalNameChanged:
      return { ...state, internalName: action.value }
    case ActionType.CoverLetterChanged:
      return { ...state, coverLetter: action.value }
    case ActionType.AttachmentsChanged:
      return { ...state, attachments: action.value, attachmentIds: action.value.map(x => x.attachmentId) }
    case ActionType.AttachmentAdded: {
      const attachments = appendAttachment(state.attachments ?? [], action.value)
      return { ...state, attachments, attachmentIds: attachments.map(x => x.attachmentId) }
    }
    case ActionType.IncludePricingTypeChanged:
      return { ...state, includePricingTypeInformation: action.value }
    case ActionType.ShowCompanyAddressChanged:
      return { ...state, showCompanyAddress: action.value }
    case ActionType.EstimateSalesTaxChanged:
      return { ...state, estimateSalesTax: action.value }
    case ActionType.RedirectToDownPaymentCheckoutOnApprovalChanged:
      return { ...state, redirectToDownPaymentCheckoutOnApproval: action.value }
    case ActionType.LineItemChanged:
      {
        const lineItems = state.lineItems.map(lineItem => {
          if (lineItem.id === action.id) {
            return lineItemReducer(lineItem, action.lineItemAction)
          }
          return lineItem
        })
        const tempState = { ...state, lineItems }
        const schedule = tempState.paymentScheduleOptions.quotedAndTimeAndMaterials.progressBased
        if (schedule.length > 0 && isPaymentPercentage(schedule[0].percentage)) {
          return updatePaymentPercentageWithParsedValue(tempState, 0, schedule[0].percentage)
        }
        return updateAmounts(tempState)
      }
    case ActionType.LineItemMoved:
      return { ...state, lineItems: moveLineItem(state.lineItems, action.id, action.over) }
    case ActionType.FinalPaymentNoteChanged:
      return {
        ...state,
        finalPaymentNote: action.value
      }
    case 'addPayment':
      return setProgressBasedPaymentSchedule(state, PaymentScheduleModel.addPaymentRow(state.paymentScheduleOptions.quotedAndTimeAndMaterials.progressBased, action.index))
    case 'deletePayment':
      return updateAmounts(setProgressBasedPaymentSchedule(state, PaymentScheduleModel.deletePaymentRow(state.paymentScheduleOptions.quotedAndTimeAndMaterials.progressBased, action.index)))
    case 'updatePaymentTitle':
      return setProgressBasedPaymentSchedule(state, PaymentScheduleModel.updatePaymentTitle(state.paymentScheduleOptions.quotedAndTimeAndMaterials.progressBased, action.index, action.value))
    case 'updatePaymentNote':
      {
        let newState = { ...state }
        const lastIndex = newState.paymentScheduleOptions.quotedAndTimeAndMaterials.progressBased.length - 1
        if (action.index === 0) {
          newState = { ...newState, downPayment: { ...state.downPayment, note: action.value } }
        }
        if (action.index === lastIndex) {
          newState = { ...newState, finalPaymentNote: action.value }
        }
        return setProgressBasedPaymentSchedule(newState, PaymentScheduleModel.updatePaymentNote(newState.paymentScheduleOptions.quotedAndTimeAndMaterials.progressBased, action.index, action.value))
      }
    case 'updatePaymentPercentage':
      if (action.index === 0) {
        const percentage = parsePercentage(action.value)
        const downPayment = isNaN(percentage) ? state.downPayment : { ...state.downPayment, percentage }
        return updatePaymentPercentage({ ...state, downPayment }, action.index, action.value)
      }
      return updatePaymentPercentage(state, action.index, action.value)
    case InfoLinkActionType.Deleted:
      return {
        ...state,
        infoLinks: state.infoLinks.filter(x => x.id !== action.id)
      }
    case InfoLinkActionType.Added:
      {
        const maxId = state.infoLinks.reduce((max, a) => a.id > max ? a.id : max, 0)
        const maxOrder = state.infoLinks.reduce((max, a) => a.order > max ? a.order : max, 0)
        return {
          ...state,
          infoLinks: [...state.infoLinks, { title: '', url: '', id: maxId + 1, order: maxOrder + 1 }]
        }
      }
    case InfoLinkActionType.TitleChanged:
      return {
        ...state,
        infoLinks: state.infoLinks.map(link => link.id === action.id ? { ...link, title: action.title } : link)
      }
    case InfoLinkActionType.UrlChanged:
      return {
        ...state,
        infoLinks: state.infoLinks.map(link => link.id === action.id ? { ...link, url: action.url } : link)
      }
    case DownPaymentActionType.AutoSubmitDownPaymentInvoiceChanged:
      return { ...state, downPayment: { ...state.downPayment, autoSubmitDownPaymentInvoice: action.value } }
    case DownPaymentActionType.EnableCardPaymentsChanged:
      return { ...state, downPayment: { ...state.downPayment, enableCardPayments: action.value } }
    case DownPaymentActionType.EnableBankTransfersChanged:
      return { ...state, downPayment: { ...state.downPayment, enableBankTransfers: action.value } }
    case DownPaymentActionType.ShowAuthorizePaymentOnFileChanged:
      return { ...state, downPayment: {
        ...state.downPayment,
        showAuthorizePaymentOnFile: action.value,
        requireAuthorizePaymentOnFile: action.value && state.downPayment.requireAuthorizePaymentOnFile
      } }
    case DownPaymentActionType.RequireAuthorizePaymentOnFileChanged:
      return { ...state, downPayment: { ...state.downPayment, requireAuthorizePaymentOnFile: action.value } }
    case DownPaymentActionType.SetPaymentDueInDaysChanged:
      return {
        ...state,
        paymentScheduleOptions: {
          ...state.paymentScheduleOptions,
          quotedAndTimeAndMaterials: {
            ...state.paymentScheduleOptions.quotedAndTimeAndMaterials,
            progressBased: state.paymentScheduleOptions.quotedAndTimeAndMaterials.progressBased
              .map((x, i) => i === 0 ? { ...x, note: renderDueDate(action.value) } : x)
          }
        },
        downPayment: {
          ...state.downPayment,
          paymentDueInDays: action.value,
          note: renderDueDate(action.value)
        }
      }
    case DownPaymentActionType.NoteChanged:
      return { ...state, downPayment: { ...state.downPayment, note: action.value } }
    case DownPaymentActionType.DownPaymentPercentageChanged:
      {
        const newValue = parsePercentage(action.value)
        const downPayment = isNaN(newValue) ? state.downPayment : { ...state.downPayment, percentage: newValue }
        return { ...state, downPayment }
      }
    case 'selectQuotedAndTimeAndMaterialsPaymentOption':
      return { ...state, paymentScheduleOptions: PaymentScheduleModel.updateQuotedAndTimeAndMaterialsSelectedOption(state.paymentScheduleOptions, action.value)}
    case 'updateIntervalBasedTotalPayments':
      return {...state, paymentScheduleOptions: PaymentScheduleModel.updateIntervalBasedTotalPayments(state.paymentScheduleOptions, action.value)}
    case 'updateIntervalBasedRecurringPayment':
      return {...state, paymentScheduleOptions: PaymentScheduleModel.updateIntervalBasedRecurringPayment(state.paymentScheduleOptions, action.value)}
    case 'updatePerVisitRecurringPayment':
      return {...state, paymentScheduleOptions: PaymentScheduleModel.updatePerVisitRecurringPayment(state.paymentScheduleOptions, action.value)}
    default:
      return state
  }
}

const initializeLineItemSortOrder = (lineItems: LineItem[]) => {
  const result = [...lineItems]
  result.sort((a, b) => a.sortOrder - b.sortOrder)
  return result.map((x, i) => ({ ...x, sortOrder: i + 1 }))
}

const moveLineItem = (lineItems: LineItem[], id: number, over: number) => {
  const from = lineItems.findIndex(x => x.id === id)
  const to = lineItems.findIndex(x => x.id === over)
  return arrayMove(lineItems, from, to).map((x, i) => ({ ...x, sortOrder: i + 1 }))
}

export const initialize = (parameters: ProposalCreationParameters): ProposalCreationParameters => {
  return {
    ...updateAmounts(parameters),
    lineItems: initializeLineItemSortOrder(parameters.lineItems),
    downPayment: initialNote(parameters.downPayment),
    finalPaymentNote: parameters.finalPaymentNote || '(due on mm/dd/yyyy)'
  }
}

export const calculateTotal = (lineItems: LineItem[]) => lineItems.filter(x => x.enabled).reduce((sum, item) => sum + item.price, 0)

export const calculateTotalsForPaymentGroups = (lineItems: LineItem[]) => {
  const totals = {
    quotedAndTimeAndMaterials: 0,
    perVisit: 0
  }

  lineItems.filter(x => x.enabled).forEach(lineItem => {
    switch (lineItem.pricingType) {
      case PricingTypes.QuotedPrice:
      case PricingTypes.TimeAndMaterials:
      case PricingTypes.FlatRate:
        totals.quotedAndTimeAndMaterials += lineItem.price
        break
      case PricingTypes.PerVisit:
        totals.perVisit += lineItem.price
        break
    }
  })

  return totals;
}

export const findUnlinkedCostCodes = (lineItems: LineItem[]) => {
  return lineItems.filter(x => x.enabled)
    .flatMap(x => x.costCodes)
    .filter(x => x.qboId === null)
    .filter((v, i, a) => a.findIndex(x => x.id == v.id) === i)
}

type State = ProposalCreationParameters

const setProgressBasedPaymentSchedule = (state: State, paymentSchedule: PaymentScheduleRow[]) => {
  return {
    ...state,
    paymentScheduleOptions: {
      ...state.paymentScheduleOptions,
      quotedAndTimeAndMaterials: {
        ...state.paymentScheduleOptions.quotedAndTimeAndMaterials,
        progressBased: paymentSchedule
      }
    }
  }
}

const updatePaymentPercentage = (state: State, index: number, value: string): State => {
  const parsedValue = roundToPrecision(parsePercentage(value), 2)
  if (isNaN(parsedValue) || parsedValue < 0) {
    return state
  }

  return updatePaymentPercentageWithParsedValue(state, index, parsedValue)
}

const getMinimumAllowedFinalPercent = (state: State) => {
  const areAllEnabledLineItemsQuotedPrice = state.lineItems
    .filter(x => x.enabled)
    .every(x => x.pricingType === PricingTypes.QuotedPrice)
  return areAllEnabledLineItemsQuotedPrice ? 0 : .01;
}

function updatePaymentPercentageWithParsedValue(state: ProposalCreationParameters, index: number, parsedValue: number) {
  const schedule = state.paymentScheduleOptions.quotedAndTimeAndMaterials.progressBased
  const minimumAllowedFinalPercent = getMinimumAllowedFinalPercent(state)

  const newPaymentSchedule = PaymentScheduleModel.updatePaymentScheduleWithNewPercentage(schedule, index, parsedValue, minimumAllowedFinalPercent)

  return updateAmounts({
    ...state,
    paymentScheduleOptions: {
      ...state.paymentScheduleOptions,
      quotedAndTimeAndMaterials: {
        ...state.paymentScheduleOptions.quotedAndTimeAndMaterials,
        progressBased: newPaymentSchedule
      }
    },
    downPayment: {
      ...state.downPayment,
      percentage: index === 0 ? newPaymentSchedule[0].percentage : state.downPayment.percentage
    }
  })
}

export const updateAmounts = (state: State): State => {
  const total = calculateTotalsForPaymentGroups(state.lineItems)
  const enabledLineItems = state.lineItems.filter(x => x.enabled && x.pricingType !== PricingTypes.PerVisit)
  let remaining = total.quotedAndTimeAndMaterials
  return {
    ...state,
    paymentScheduleOptions: {
      ...state.paymentScheduleOptions,
      quotedAndTimeAndMaterials: {
        ...state.paymentScheduleOptions.quotedAndTimeAndMaterials,
        progressBased: state.paymentScheduleOptions.quotedAndTimeAndMaterials.progressBased.map((x, i, array) => {
          const amount = i === array.length - 1
            ? remaining
            : enabledLineItems.reduce((total, lineItem) => total + Math.round(lineItem.price * x.percentage), 0)
          remaining -= amount
          return { ...x, amount }
        })
      }
    }
  }
}

export enum DownPaymentActionType {
  AutoSubmitDownPaymentInvoiceChanged = 'autoSubmitDownPaymentInvoiceChanged',
  EnableCardPaymentsChanged = 'enableCardPaymentsChanged',
  EnableBankTransfersChanged = 'enableBankTransfersChanged',
  ShowAuthorizePaymentOnFileChanged = 'showAuthorizePaymentOnFileChanged',
  RequireAuthorizePaymentOnFileChanged = 'requireAuthorizePaymentOnFileChanged',
  SetPaymentDueInDaysChanged = 'setPaymentDueInDaysChanged',
  NoteChanged = 'noteChanged',
  DownPaymentPercentageChanged = "DownPaymentPercentageChanged",
}

export const setAutoSubmitDownPaymentInvoice = (value: boolean): Action => ({ type: DownPaymentActionType.AutoSubmitDownPaymentInvoiceChanged, value })
export const setEnableCardPayments = (value: boolean): Action => ({ type: DownPaymentActionType.EnableCardPaymentsChanged, value })
export const setEnableBankTransfers = (value: boolean): Action => ({ type: DownPaymentActionType.EnableBankTransfersChanged, value })
export const setShowAuthorizePaymentOnFileChanged = (value: boolean): Action => ({ type: DownPaymentActionType.ShowAuthorizePaymentOnFileChanged, value })
export const setRequireAuthorizePaymentOnFileChanged = (value: boolean): Action => ({ type: DownPaymentActionType.RequireAuthorizePaymentOnFileChanged, value })
export const setPaymentDueInDays = (value: number): Action => ({ type: DownPaymentActionType.SetPaymentDueInDaysChanged, value })
export const setNote = (value: string): Action => ({ type: DownPaymentActionType.NoteChanged, value })

export const setDownPaymentPercentage = (value: string): Action => ({ type: DownPaymentActionType.DownPaymentPercentageChanged, value })

export type CostCode = {
  name: string,
  id: number,
}

export interface DownPaymentParameters {
  autoSubmitDownPaymentInvoice: boolean
  enableCardPayments: boolean
  enableBankTransfers: boolean
  paymentDueInDays: number
  note: string
  percentage: number
  costCodesUnlinkedToQbo: CostCode[]
  isCustomerLinkedToQbo: boolean
  invoicingSystem: InvoicingSystem
  showAuthorizePaymentOnFile: boolean
  requireAuthorizePaymentOnFile: boolean
}

export enum DueDateOption {
  OnSigning = 0,
  FifteenDays = 15,
  ThirtyDays = 30,
  FortyFiveDays = 45,
  NinetyDays = 90,
  Custom = -1
}

export const numberToDueDateOption = (n: number): DueDateOption => {
  switch (n) {
    case DueDateOption.OnSigning: return DueDateOption.OnSigning
    case DueDateOption.FifteenDays: return DueDateOption.FifteenDays
    case DueDateOption.ThirtyDays: return DueDateOption.ThirtyDays
    case DueDateOption.FortyFiveDays: return DueDateOption.FortyFiveDays
    case DueDateOption.NinetyDays: return DueDateOption.NinetyDays
    default: return DueDateOption.Custom
  }
}
export const dueDateOptionToNumber = (o: DueDateOption) => o as number

export const renderDueDate = (days: number) => {
  const dueDateOption = numberToDueDateOption(days)
  const found = dueDateOptions.find(([, item]) => dueDateOption === item)
  return found![1] === DueDateOption.Custom
    ? (days === 1 ? `Due in ${days} day` : `Due in ${days} days`)
    : found![0]
}

export const dueDateOptions: [string, DueDateOption][] = [
  ["Due immediately", DueDateOption.OnSigning],
  ["Due in 15 days", DueDateOption.FifteenDays],
  ["Due in 30 days", DueDateOption.ThirtyDays],
  ["Due in 45 days", DueDateOption.FortyFiveDays],
  ["Due in 90 days", DueDateOption.NinetyDays],
  ["Custom Due in", DueDateOption.Custom]
]

export const initialNote = (downPayment: DownPaymentParameters): DownPaymentParameters => {
  return downPayment.note
    ? downPayment
    : reducer({ ...defaultState(), downPayment }, setPaymentDueInDays(downPayment.paymentDueInDays)).downPayment
}

export const blankDownPayment = (paymentDueInDays = 0): DownPaymentParameters => {
  return {
    autoSubmitDownPaymentInvoice: false,
    enableBankTransfers: false,
    enableCardPayments: false,
    paymentDueInDays: paymentDueInDays,
    note: renderDueDate(paymentDueInDays),
    percentage: 0,
    isCustomerLinkedToQbo: true,
    costCodesUnlinkedToQbo: [],
    invoicingSystem: 'quickBooks',
    showAuthorizePaymentOnFile: false,
    requireAuthorizePaymentOnFile: false
  }
}

export const parsePercentage = (input: string): number => {
  const value = parseFloat(input) / 100
  return isPaymentPercentage(value) ? value : NaN
}

/** Percentages are stored as a 0-1 fraction, so anything outside that is data we cannot save. */
export const isPaymentPercentage = (value: number): boolean => !isNaN(value) && 0 <= value && value <= 1

export const isAutoSubmitDisabled = (isCustomerLinkedToQbo: boolean, costCodesUnlinkedToQbo: any[]): boolean =>
  !isCustomerLinkedToQbo || costCodesUnlinkedToQbo.length > 0

/**
 * The api rejects paid-upfront unless the schedule is exactly one row, so the payload swaps the
 * user's milestones for that row at the last moment and leaves state alone - which is what lets
 * them switch back to Progress-based and still have their schedule (SUFT-241). The note comes from
 * the down payment because the due-date control now writes to the schedule the drawer is showing.
 */
const progressBasedForSave = (state: ProposalCreationParameters): PaymentScheduleRow[] => {
  const { selectedOption, progressBased } = state.paymentScheduleOptions.quotedAndTimeAndMaterials
  if (selectedOption !== 'paid-upfront') {
    return progressBased
  }
  const [row] = PaymentScheduleModel.paidUpfrontSchedule()
  return [{ ...row, note: state.downPayment.note }]
}

export const preparePayloadForSave = (state: ProposalCreationParameters): ProposalCreationParameters => {
  const autoSubmitDownPayment = optionOffersAutoSubmit(state.paymentScheduleOptions.quotedAndTimeAndMaterials.selectedOption)
    && state.downPayment.autoSubmitDownPaymentInvoice
  return {
    ...state,
    downPayment: {
      ...state.downPayment,
      autoSubmitDownPaymentInvoice: autoSubmitDownPayment
    },
    paymentScheduleOptions: {
      ...state.paymentScheduleOptions,
      quotedAndTimeAndMaterials: {
        ...state.paymentScheduleOptions.quotedAndTimeAndMaterials,
        progressBased: progressBasedForSave(state)
      }
    },
    lineItems: state.lineItems.map(x => ({
      ...x,
      selectedItemGuids: x.showItems ? x.selectedItemGuids : [],
      selectedItemColumns: x.showItems ? x.selectedItemColumns : [],
      attachmentIds: (x.attachments ?? []).map(a => a.attachmentId)
    }))
  }
}

export type ProposalDrawerMode = {
  proposalId?: number
  jobId?: number
}

export const isCreatingProposal = ({ proposalId }: ProposalDrawerMode): boolean => proposalId === undefined

export type SaveGate = ProposalDrawerMode & {
  isInitialized: boolean
  isLoading: boolean
  isSaving: boolean
  hasShownWorkArea: boolean
}

/**
 * A proposal with nothing shown is rejected by the api ("Select at least one workarea."),
 * so the drawer blocks it up front rather than letting the request fail (SYN-3819).
 * A job with no work areas at all is a different situation and is deliberately not gated
 * here — the server still has the final say.
 */
export const hasShownWorkArea = (lineItems: Pick<LineItem, 'enabled'>[]): boolean =>
  lineItems.length === 0 || lineItems.some(x => x.enabled)

export const optionOffersAutoSubmit = (selectedOption: string): boolean =>
  selectedOption === 'progress-based' || selectedOption === 'paid-upfront'

export const canSaveProposal = (gate: SaveGate): boolean => {
  if (!gate.isInitialized || gate.isLoading || gate.isSaving || !gate.hasShownWorkArea) {
    return false
  }
  return isCreatingProposal(gate) ? gate.jobId !== undefined : gate.proposalId !== undefined
}
